import { BusinessHoursDto, MenuHoursDto, MerchantMenuDto, OrderTypeIndexEnum } from '@up/data';
import { WebEnvironmentSettings } from '../../../../interfaces/src/lib/web-environment-settings';
import { timeToDate } from '../extensions/date.helper';

export function isMenuAvailable(menu: MerchantMenuDto, date?: Date): boolean {
  const targetDate = date || new Date();
  const targetWeekday = (targetDate.getUTCDay() + 6) % 7;

  const matchingMenuHour = menu.menuHours.find(menuHours => menuHours.weekday === targetWeekday);

  return matchingMenuHour ? matchingMenuHour.isAvailable : false;
}

export function isMenuAvailableByOrderType(menu: MerchantMenuDto, date?: Date, orderTypeId?: number): boolean {
  const targetDate = date || new Date();
  const targetWeekday = (targetDate.getUTCDay() + 6) % 7;

  const matchingMenuHour = menu.menuHours.find(menuHours => 
    !menuHours.isOverNight && menuHours.weekday === targetWeekday && (!orderTypeId || menuHours.orderTypeId === orderTypeId)
  );

  return matchingMenuHour ? matchingMenuHour.isAvailable : false;
}

export function isMenuWithOrderTypeId(menu: MerchantMenuDto, orderTypeId: number, date?: Date): boolean {
  const targetDate = date || new Date();
  const targetWeekday = (targetDate.getUTCDay() + 6) % 7;

  const matchingMenuHour = menu.menuHours.some(menuHours => 
    menuHours.weekday === targetWeekday && (!orderTypeId || menuHours.orderTypeId === orderTypeId)
  );

  return matchingMenuHour;
}

export function isMenuWithOrderType(menu: MerchantMenuDto, date?: Date): boolean {
  const targetDate = date || new Date();
  const targetWeekday = (targetDate.getUTCDay() + 6) % 7;

  const matchingMenuHour = menu.menuHours.some(menuHours => 
    menuHours.weekday === targetWeekday && menuHours.orderTypeId
  );

  return matchingMenuHour;
}

export function getMenusWithOrderType(menuHours: MenuHoursDto[], date?: Date): MenuHoursDto[] {
  const targetDate = date || new Date();
  const targetWeekday = (targetDate.getUTCDay() + 6) % 7;
  
  const enumsValues = Object.values(OrderTypeIndexEnum);
  
  const matchingMenuHours = menuHours.filter(menuHours => 
    menuHours.weekday === targetWeekday && enumsValues.includes(menuHours.orderTypeId)
  );
  
  return matchingMenuHours;
}

export function getMenuHoursStartTimeForDay(
  menu: MerchantMenuDto,
  date: Date = new Date(),
  orderTypeId?: number
): Date {
  for (const menuHours of menu.menuHours) {
    if (
      !menuHours.isOverNight &&
      menuHours.weekday === (date.getUTCDay() + 6) % 7 && 
      menuHours.isAvailable &&
      (!orderTypeId || menuHours.orderTypeId === orderTypeId)
    ) {
      return timeToDate(menuHours.startTime, true, date);
    }
  }
}

export function getMenuHoursOverNightStartTimeForDay(
    menuHours: MenuHoursDto,
    currentDate: Date,
  ): Date {
    if (menuHours.isAvailable) {
      const currentDateUTCDay = currentDate.getUTCDay();
      const weekDayNow = (currentDateUTCDay + 6) % 7;
      if(
        //check for the monday weekday and menuHours for sunday weekday
        (weekDayNow === 0 && menuHours.weekday === 6) ||
        //check for the previous weekday, monday not checked
        (weekDayNow !== 0 && (weekDayNow - 1) === menuHours.weekday)
      ){
        const startTime = timeToDate(menuHours.startTime, true, currentDate);
        startTime.setUTCDate(startTime.getUTCDate() - 1);
        return startTime;
      }
            
      if(
        //check for the monday weekday with menuHours
        (weekDayNow === 0 && menuHours.weekday === 0) ||
        //check for the current day
        (weekDayNow === menuHours.weekday)
      ){
        return timeToDate(menuHours.startTime, true, currentDate);
      }
    }
  }

  export function getMenuHoursEndTimeForDay(
    menu: MerchantMenuDto,
    date: Date = new Date(),
    orderTypeId?: number
  ): Date {
    for (const menuHours of menu.menuHours) {
      if (
        !menuHours.isOverNight &&
        menuHours.weekday === (date.getUTCDay() + 6) % 7 && 
        menuHours.isAvailable &&
        (!orderTypeId || menuHours.orderTypeId === orderTypeId)
      ) {
        const endTime = timeToDate(menuHours.endTime, true, date)
  
        if(endTime.getHours() === 0) {
          endTime.setDate(endTime.getDate() + 1);
        }
  
        return endTime;
      }
    }
  }

  export function getMenuHoursOverNightEndTimeForDay(
    menuHours: MenuHoursDto,
    currentDate: Date
  ): Date {
    if (menuHours.isAvailable) {
      const currentDateUTCDay = currentDate.getUTCDay();
      const weekDayNow = (currentDateUTCDay + 6) % 7;
      
      if (
        (weekDayNow === 0 && menuHours.weekday === 6) || // Check for the Monday weekday and menuHours for Sunday weekday
        (weekDayNow !== 0 && (weekDayNow - 1) === menuHours.weekday) // Check for the previous weekday, Monday not checked
      ) {
        return timeToDate(menuHours.endTime, true, currentDate);
      }
      
      if (
        (weekDayNow === 0 && menuHours.weekday === 0) || // Check for the Monday weekday with menuHours
        (weekDayNow === menuHours.weekday) // Check for the current day
      ) {
        const mondayOrCurrentDayEndTime = timeToDate(menuHours.endTime, true, currentDate);
        
        // Ensure it's the same day before adding a day
        if (isSameDay(mondayOrCurrentDayEndTime, currentDate)) {
          mondayOrCurrentDayEndTime.setUTCDate(mondayOrCurrentDayEndTime.getUTCDate() + 1);
        }

        if (mondayOrCurrentDayEndTime > currentDate) 
          return mondayOrCurrentDayEndTime;
      }
    }
  }

  export function getMenuHoursOverNightEndTimeForPreviousDay(
    menuHours: MenuHoursDto,
    currentDate: Date
  ): Date {
    if (menuHours.isAvailable) {
      const currentDateUTCDay = currentDate.getUTCDay();
      const weekDayNow = (currentDateUTCDay + 6) % 7;
      let addDay = false;
  
      if (
        //check for the current day
        weekDayNow !== menuHours.weekday
      ) {
        addDay = true;
      }
  
      const endTime = timeToDate(menuHours.endTime, addDay);
  
      if (menuHours.endTime.hours < menuHours.startTime.hours && !addDay) {
        endTime.setUTCDate(endTime.getUTCDate() + 1);
      }
  
      return endTime;
    }
  }

  /*
  * Function to return only menu hour overnight of the menu for web ordering apps
  */
  export function getMenuHoursOverNight(menu: MerchantMenuDto, currentDate: Date, orderTypeId?: number) {
    const menuHourOverNight = [];
    const currentDateUTCDay = currentDate.getUTCDay();
    const weekDayNow = (currentDateUTCDay + 6) % 7;

    if ((menu.isWebOrdering || menu.isOrderAtTable || menu.isUltraOrder || menu.isCatering) &&
        new Date(menu.startDate) <= currentDate && (!menu.endDate || new Date(menu.endDate) > currentDate)) {

        menu?.menuHours?.forEach(menuHour => {
            if (menuHour.isAvailable && menuHour.isOverNight && (!orderTypeId || menuHour.orderTypeId === orderTypeId)) {
                if (
                    weekDayNow === menuHour.weekday // Current day's menu hour
                ) {
                    const endTime = timeToDate(menuHour.endTime, true, currentDate);

                    if (weekDayNow === menuHour.weekday) {
                        endTime.setUTCDate(endTime.getUTCDate() + 1);
                    }

                    if (endTime > currentDate) {
                        menuHourOverNight.push(menuHour);
                    }
                }
            }
        });
    }

    return menuHourOverNight;
  }

  export function getMenuHoursOverNightPreviousDay(
    menu: MerchantMenuDto,
    currentDate: Date
) {
    const menuHourOverNight = [];
    const currentDateUTCDay = currentDate.getUTCDay();
    const weekDayNow = (currentDateUTCDay + 6) % 7;

    const currentDayMenuHour = menu.menuHours?.find(
        (menuHour) =>
            menuHour.isAvailable && menuHour.isOverNight &&
            //check for the current day
            (weekDayNow === menuHour.weekday)
    );

    if (currentDayMenuHour && 
        menu.isWebOrdering && 
        new Date(menu.startDate) < currentDate && 
        (!menu.endDate || new Date(menu.endDate) > currentDate)
    ) {
        const currentDayEndTime = timeToDate(currentDayMenuHour.endTime, true);
        currentDayEndTime.setUTCDate(currentDayEndTime.getUTCDate() + 1);

        if(currentDayEndTime > currentDate) {
            menuHourOverNight.push(currentDayMenuHour);
        }
    }

    return menuHourOverNight;
}

  /*
  * Function to return only menu hour over night of the menu where the flaf isKiosk is true.
  */
  export function getMenuHoursOverNightForWebKiosk(
    menu: MerchantMenuDto,
    currentDate: Date
  ) {
    const menuHourOverNight= [];
    const currentDateUTCDay = currentDate.getUTCDay();
    const weekDayNow = (currentDateUTCDay + 6) % 7;

    const sundayOrPreviousDayMenuHour = menu.menuHours?.find(
      (menuHour) =>
        menuHour.isAvailable && menuHour.isOverNight && 
        (
          //check for the monday weekday and menuHour for sunday weekday
          (weekDayNow === 0 && menuHour.weekday === 6) ||
          //check for the previous weekday, monday not checked
          (weekDayNow !== 0 && (weekDayNow - 1) === menuHour.weekday)
        )
    );

    if (
      sundayOrPreviousDayMenuHour && 
        menu.isKiosk && 
        new Date(menu.startDate) < currentDate && 
        (!menu.endDate || new Date(menu.endDate) > currentDate)
      ) 
    {
      const endTime = timeToDate(sundayOrPreviousDayMenuHour.endTime, true);

      if(endTime > currentDate) {
        menuHourOverNight.push(sundayOrPreviousDayMenuHour); 
      }
    }

    const mondayOrCurrentDayMenuHour = menu.menuHours?.find(
      (menuHour) =>
        menuHour.isAvailable && menuHour.isOverNight && 
          (
            //check for the monday weekday with menuHour
            (weekDayNow === 0 && menuHour.weekday === 0) ||
            //check for the current day
            (weekDayNow === menuHour.weekday)
          )
    );

    if(mondayOrCurrentDayMenuHour) {
      const mondayOrCurrentDayEndTime = timeToDate(mondayOrCurrentDayMenuHour.endTime, true);
      mondayOrCurrentDayEndTime.setUTCDate(mondayOrCurrentDayEndTime.getUTCDate() + 1);

      if(mondayOrCurrentDayEndTime > currentDate)
      menuHourOverNight.push(mondayOrCurrentDayMenuHour);
    }

    return menuHourOverNight;
  }

  export function getValidMenuHourOverNight(
    menuHoursOverNight: MenuHoursDto[],
    currentDate: Date
  ): MenuHoursDto {
    let validMenuHour;
    const currentDateUTCDay = currentDate.getUTCDay();
    const weekDayNow = (currentDateUTCDay + 6) % 7;
    const sundayMenuHour = menuHoursOverNight.find((x) => x.weekday === 6 && weekDayNow === 0);
    if (sundayMenuHour) {
      validMenuHour = sundayMenuHour;
    } else {
      // We will return the first menu hour if there is no Sunday menu hour
      validMenuHour = menuHoursOverNight.length > 0 ? menuHoursOverNight[0] : null;
    }
    return validMenuHour;
  }

  export function checkForValidMenuHourOverNight(
    menuHoursOverNight: MenuHoursDto[],
    businessHoursOverNight: BusinessHoursDto[],
    currentDate: Date,
    todayOvernight?: boolean
  ): MenuHoursDto {
    let validMenuHour;
    const currentDateUTCDay = currentDate.getUTCDay();
    const weekDayNow = (currentDateUTCDay + 6) % 7;
    const sundayMenuHour = menuHoursOverNight.find((x) => x.weekday === 6 && weekDayNow === 0);
    const sundayBusinessHour = businessHoursOverNight.find((x) => x.weekday === 6 && weekDayNow === 0);
    if (sundayMenuHour && sundayBusinessHour) {
      validMenuHour = sundayMenuHour;
    } else {
      const findValidHours = (x, menuHour) =>
        todayOvernight
          ? (x.weekday + 1) === menuHour.weekday
          : x.weekday === menuHour.weekday;
      
      menuHoursOverNight.forEach((menuHour) => {
        if(!validMenuHour && businessHoursOverNight.find((x) => findValidHours(x, menuHour))) {
          validMenuHour = menuHour;
        }
      })  
    }
    return validMenuHour;
  }

  export function checkForValidMenuHourOverNightV2(
    menuHoursOverNight: MenuHoursDto[],
    currentDate: Date,
    todayOvernight?: boolean
  ): MenuHoursDto | undefined {
    let validMenuHour;
    const currentDateUTCDay = currentDate.getUTCDay();
    const weekDayNow = (currentDateUTCDay + 6) % 7;
    const sundayMenuHour = menuHoursOverNight.find((x) => x.weekday === 6 && weekDayNow === 0);
    
    if (sundayMenuHour) {
      validMenuHour = sundayMenuHour;
    } else {
      const findValidHours = (menuHour) =>
        todayOvernight ? ((weekDayNow + 1) % 7) === menuHour.weekday : weekDayNow === menuHour.weekday;
      
      validMenuHour = menuHoursOverNight.find((menuHour) => findValidHours(menuHour));
    }
  
    return validMenuHour;
  }

  export function isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  export function isOrderAtTableAndHasNoHours(
    menu: MerchantMenuDto,
    envSettings: Partial<WebEnvironmentSettings>,
    isOrderAtTableEnabled: boolean,
    date?: Date
  ): boolean {
    const targetDate = date || new Date();
    return (
      !isMenuWithOrderTypeId(menu, OrderTypeIndexEnum.TABLE, targetDate) &&
      isOrderAtTableEnabled &&
      envSettings.isOrderAtTable
    );
  }
  